import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Eye, EyeOff, Trash2, UserX } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { removeUserCompletely, markUserAsDeleted } from "@/lib/utils/completeUserDeletion";

interface CompleteDeletionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmDeactivate: () => void;
  onConfirmComplete: () => void;
  userToDelete: {
    id: string;
    name: string;
    email: string;
    role: string;
  } | null;
}

export function CompleteDeletionDialog({
  isOpen,
  onClose,
  onConfirmDeactivate,
  onConfirmComplete,
  userToDelete,
}: CompleteDeletionDialogProps) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [deletionType, setDeletionType] = useState<'deactivate' | 'complete'>('deactivate');
  const { toast } = useToast();

  const handleVerifyPassword = async () => {
    if (!password.trim()) {
      toast({
        title: "Password Required",
        description: "Please enter your password to confirm deletion.",
        variant: "destructive",
      });
      return;
    }

    setIsVerifying(true);
    try {
      // Get current user
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      
      if (!currentUser) {
        throw new Error("No authenticated user found");
      }

      // Verify password by attempting to sign in
      const { error } = await supabase.auth.signInWithPassword({
        email: currentUser.email!,
        password: password,
      });

      if (error) {
        throw new Error("Invalid password");
      }

      // Password is correct, proceed with deletion
      toast({
        title: "Password Verified",
        description: "Password confirmed. Proceeding with user deletion.",
      });

      if (deletionType === 'complete') {
        onConfirmComplete();
      } else {
        onConfirmDeactivate();
      }
      
      handleClose();
    } catch (error: any) {
      toast({
        title: "Password Verification Failed",
        description: error.message || "Invalid password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleClose = () => {
    setPassword("");
    setShowPassword(false);
    setIsVerifying(false);
    setDeletionType('deactivate');
    onClose();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isVerifying) {
      handleVerifyPassword();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            Choose Deletion Type
          </DialogTitle>
          <DialogDescription>
            Select how you want to handle user <strong>{userToDelete?.name}</strong> ({userToDelete?.email}).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {userToDelete && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <h4 className="font-semibold text-red-800 mb-2">User to be processed:</h4>
              <div className="text-sm text-red-700">
                <p><strong>Name:</strong> {userToDelete.name}</p>
                <p><strong>Email:</strong> {userToDelete.email}</p>
                <p><strong>Role:</strong> {userToDelete.role}</p>
              </div>
            </div>
          )}

          {/* Deletion Type Selection */}
          <div className="space-y-3">
            <Label>Choose deletion type:</Label>
            
            <div className="space-y-2">
              <div 
                className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                  deletionType === 'deactivate' 
                    ? 'border-orange-500 bg-orange-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setDeletionType('deactivate')}
              >
                <div className="flex items-center gap-2">
                  <UserX className="h-4 w-4 text-orange-600" />
                  <div>
                    <div className="font-medium text-orange-800">Deactivate User</div>
                    <div className="text-sm text-orange-600">
                      Mark as inactive, remove from doctors table. User cannot login but data remains.
                    </div>
                  </div>
                </div>
              </div>

              <div 
                className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                  deletionType === 'complete' 
                    ? 'border-red-500 bg-red-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setDeletionType('complete')}
              >
                <div className="flex items-center gap-2">
                  <Trash2 className="h-4 w-4 text-red-600" />
                  <div>
                    <div className="font-medium text-red-800">Complete Deletion</div>
                    <div className="text-sm text-red-600">
                      Delete from ALL tables including authentication. User completely removed.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Enter your password to confirm:</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter your admin password"
                disabled={isVerifying}
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isVerifying}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          <div className={`p-3 border rounded-lg ${
            deletionType === 'complete' 
              ? 'bg-red-50 border-red-200' 
              : 'bg-yellow-50 border-yellow-200'
          }`}>
            <p className={`text-sm ${
              deletionType === 'complete' ? 'text-red-800' : 'text-yellow-800'
            }`}>
              <strong>Warning:</strong> {
                deletionType === 'complete' 
                  ? 'This will permanently delete the user from ALL tables including authentication. This action cannot be undone!'
                  : 'This will mark the user as inactive and remove them from the doctors table (if applicable), preventing them from logging in.'
              }
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isVerifying}
          >
            Cancel
          </Button>
          <Button
            variant={deletionType === 'complete' ? 'destructive' : 'default'}
            onClick={handleVerifyPassword}
            disabled={isVerifying || !password.trim()}
            className="flex items-center gap-2"
          >
            {isVerifying ? "Verifying..." : (
              <>
                {deletionType === 'complete' ? <Trash2 className="h-4 w-4" /> : <UserX className="h-4 w-4" />}
                {deletionType === 'complete' ? 'Delete Completely' : 'Deactivate User'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
